import type { ReactNode, Dispatch, SetStateAction } from 'react';
import React, { useMemo } from 'react';

interface DebugMessage {
    message: string;
    stack?: string;
}
interface UseDebugHook {
    add: (message: DebugMessage) => void;
    clear: () => void;
    messages: DebugMessage[];
}

interface DebugData {
    messages: DebugMessage[];
    setMessages: Dispatch<SetStateAction<DebugMessage[]>>;
}

const DebugContext = React.createContext<DebugData | undefined>(undefined);

/**
 * Hook to get, add, and clear the error messages in the error boundary.
 */
export const useDebug = (): UseDebugHook => {
    const context = React.useContext(DebugContext);
    if (!context) {
        throw new Error('Cannot be used outside DebugContext');
    }
    const { messages, setMessages } = context;
    return useMemo(() => {
        if (import.meta.env.PROD) {
            return {
                add() {},
                clear() {},
                messages: [],
            };
        }
        return {
            add(message) {
                setMessages((current) => [...current, message]);
            },
            clear() {
                setMessages([]);
            },
            messages,
        };
    }, [messages, setMessages]);
};

interface Props {
    children: ReactNode;
}

export const DebugContextProvider = ({ children }: Props) => {
    const [messages, setMessages] = React.useState<DebugMessage[]>([]);
    return (
        <DebugContext.Provider
            value={{
                messages,
                setMessages,
            }}
        >
            {children}
        </DebugContext.Provider>
    );
};
